import { type RefrigerationUnit } from '@/data/schemas';
import type { RefrigerationUnitTechnicalInput } from '@/data/repos/refrigerationUnitsRepo';
import { toOptionalInt, toOptionalNumber, trim, type RefrigerationUnitFormValues } from './shared';

export const buildVentilationDefaults = (): Pick<
  RefrigerationUnitFormValues,
  | 'ventilationType'
  | 'ventilationFlowRateM3h'
  | 'ventilationNotes'
  | 'ventilationNaturalHighM2'
  | 'ventilationNaturalLowM2'
  | 'ventilationNaturalNotes'
  | 'ventilationExtractUnits'
  | 'ventilationExtractLogic'
  | 'ventilationExtractMode'
  | 'ventilationExtractAtex'
  | 'ventilationExtractFlowM3h'
  | 'ventilationExtractOperation'
  | 'scrubberPresent'
  | 'scrubberNotes'
  | 'scrubberAirFlowM3h'
  | 'scrubberWaterFlowM3h'
  | 'scrubberWaterFlowSpec'
  | 'scrubberContactTimeSeconds'
  | 'scrubberWaterSupply'
  | 'scrubberWasteCollection'
  | 'scrubberAtex'
> => ({
  ventilationType: '',
  ventilationFlowRateM3h: null,
  ventilationNotes: '',
  ventilationNaturalHighM2: null,
  ventilationNaturalLowM2: null,
  ventilationNaturalNotes: '',
  ventilationExtractUnits: null,
  ventilationExtractLogic: '',
  ventilationExtractMode: '',
  ventilationExtractAtex: false,
  ventilationExtractFlowM3h: null,
  ventilationExtractOperation: '',
  scrubberPresent: false,
  scrubberNotes: '',
  scrubberAirFlowM3h: null,
  scrubberWaterFlowM3h: null,
  scrubberWaterFlowSpec: '',
  scrubberContactTimeSeconds: null,
  scrubberWaterSupply: '',
  scrubberWasteCollection: '',
  scrubberAtex: false,
});

export const buildVentilationValues = (
  refrigerationUnit: RefrigerationUnit
): Pick<
  RefrigerationUnitFormValues,
  | 'ventilationType'
  | 'ventilationFlowRateM3h'
  | 'ventilationNotes'
  | 'ventilationNaturalHighM2'
  | 'ventilationNaturalLowM2'
  | 'ventilationNaturalNotes'
  | 'ventilationExtractUnits'
  | 'ventilationExtractLogic'
  | 'ventilationExtractMode'
  | 'ventilationExtractAtex'
  | 'ventilationExtractFlowM3h'
  | 'ventilationExtractOperation'
  | 'scrubberPresent'
  | 'scrubberNotes'
  | 'scrubberAirFlowM3h'
  | 'scrubberWaterFlowM3h'
  | 'scrubberWaterFlowSpec'
  | 'scrubberContactTimeSeconds'
  | 'scrubberWaterSupply'
  | 'scrubberWasteCollection'
  | 'scrubberAtex'
> => {
  const ventilation = refrigerationUnit.technical?.safety?.ventilation;
  const naturalVentilation = ventilation?.natural;
  const extractors = ventilation?.extractors;
  const scrubber = refrigerationUnit.technical?.safety?.scrubber;

  return {
    ventilationType: ventilation?.type ?? '',
    ventilationFlowRateM3h: ventilation?.flowRateM3h ?? null,
    ventilationNotes: ventilation?.notes ?? '',
    ventilationNaturalHighM2: naturalVentilation?.highOpeningsAreaM2 ?? null,
    ventilationNaturalLowM2: naturalVentilation?.lowOpeningsAreaM2 ?? null,
    ventilationNaturalNotes: naturalVentilation?.notes ?? '',
    ventilationExtractUnits: extractors?.units ?? null,
    ventilationExtractLogic: extractors?.logic ?? '',
    ventilationExtractMode: extractors?.mode ?? '',
    ventilationExtractAtex: extractors?.atex ?? false,
    ventilationExtractFlowM3h: ventilation?.extractorsFlowM3h ?? extractors?.airflowM3h ?? null,
    ventilationExtractOperation:
      ventilation?.extractorsOperation ?? extractors?.logic ?? extractors?.mode ?? '',
    scrubberPresent: scrubber?.present ?? false,
    scrubberNotes: scrubber?.notes ?? '',
    scrubberAirFlowM3h: scrubber?.airFlowM3h ?? null,
    scrubberWaterFlowM3h: scrubber?.waterFlowM3h ?? null,
    scrubberWaterFlowSpec: scrubber?.waterFlowSpec ?? '',
    scrubberContactTimeSeconds: scrubber?.contactTimeSeconds ?? null,
    scrubberWaterSupply: scrubber?.waterSupply ?? '',
    scrubberWasteCollection: scrubber?.wasteCollection ?? '',
    scrubberAtex: scrubber?.atex ?? false,
  };
};

export const ventilationToInput = (
  values: RefrigerationUnitFormValues
): Pick<NonNullable<RefrigerationUnitTechnicalInput['safety']>, 'ventilation' | 'scrubber'> => {
  const ventilationType = trim(values.ventilationType);
  const ventilationFlow = toOptionalNumber(values.ventilationFlowRateM3h ?? undefined);
  const ventilationNotes = trim(values.ventilationNotes);
  const naturalHigh = toOptionalNumber(values.ventilationNaturalHighM2 ?? undefined);
  const naturalLow = toOptionalNumber(values.ventilationNaturalLowM2 ?? undefined);
  const naturalNotes = trim(values.ventilationNaturalNotes);
  const ventilationNatural =
    naturalHigh !== undefined || naturalLow !== undefined || naturalNotes
      ? {
          highOpeningsAreaM2: naturalHigh,
          lowOpeningsAreaM2: naturalLow,
          notes: naturalNotes,
        }
      : undefined;

  const extractorUnits = toOptionalInt(values.ventilationExtractUnits ?? undefined);
  const extractorLogic = trim(values.ventilationExtractLogic);
  const extractorMode = trim(values.ventilationExtractMode);
  const extractorFlow = toOptionalNumber(values.ventilationExtractFlowM3h ?? undefined);
  const extractorOperation = trim(values.ventilationExtractOperation);
  const hasExtractors =
    extractorUnits !== undefined ||
    extractorLogic ||
    extractorMode ||
    extractorFlow !== undefined ||
    values.ventilationExtractAtex;

  const hasVentilation =
    ventilationType ||
    ventilationFlow !== undefined ||
    ventilationNotes ||
    ventilationNatural ||
    hasExtractors;

  const ventilation = hasVentilation
    ? {
        type: ventilationType,
        flowRateM3h: ventilationFlow,
        notes: ventilationNotes,
        naturalPresent: ventilationNatural ? true : undefined,
        apertureHighM2: naturalHigh,
        apertureLowM2: naturalLow,
        natural: ventilationNatural,
        extractors: hasExtractors
          ? {
              units: extractorUnits,
              airflowM3h: extractorFlow,
              atex: values.ventilationExtractAtex,
              logic: extractorLogic,
              mode: extractorMode,
            }
          : undefined,
        extractorsCount: extractorUnits,
        extractorsFlowM3h: extractorFlow,
        extractorsOperation: extractorOperation,
        extractorsAtex: values.ventilationExtractAtex,
      }
    : undefined;

  const scrubber = values.scrubberPresent
    ? {
        present: true,
        notes: trim(values.scrubberNotes),
        airFlowM3h: toOptionalNumber(values.scrubberAirFlowM3h ?? undefined),
        waterFlowM3h: toOptionalNumber(values.scrubberWaterFlowM3h ?? undefined),
        waterFlowSpec: trim(values.scrubberWaterFlowSpec),
        contactTimeSeconds: toOptionalNumber(values.scrubberContactTimeSeconds ?? undefined),
        waterSupply: trim(values.scrubberWaterSupply),
        wasteCollection: trim(values.scrubberWasteCollection),
        atex: values.scrubberAtex,
      }
    : { present: false };

  return { ventilation, scrubber };
};
